import _classCallCheck from "D:\\Projects\\SourceCode\\MOHAP\\BS.MOHAP.Webchat\\BS.MOHAP.Webchat\\main\\node_modules\\@babel\\runtime/helpers/esm/classCallCheck";
import _createClass from "D:\\Projects\\SourceCode\\MOHAP\\BS.MOHAP.Webchat\\BS.MOHAP.Webchat\\main\\node_modules\\@babel\\runtime/helpers/esm/createClass";
// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT license.
import { AudioStreamFormat } from "../../src/sdk/Audio/AudioStreamFormat";
import { connectivity, type } from "../common.speech/Exports";
import { AudioSourceErrorEvent, AudioSourceInitializingEvent, AudioSourceOffEvent, AudioSourceReadyEvent, AudioStreamNodeAttachedEvent, AudioStreamNodeAttachingEvent, AudioStreamNodeDetachedEvent, AudioStreamNodeErrorEvent, createNoDashGuid, Events, EventSource, PromiseHelper, Stream } from "../common/Exports";
export var FileAudioSource = /*#__PURE__*/function () {
  function FileAudioSource(file, audioSourceId) {
    var _this = this;

    _classCallCheck(this, FileAudioSource);

    this.privStreams = {};

    this.turnOn = function () {
      if (typeof FileReader === "undefined") {
        var errorMsg = "Browser does not support FileReader.";

        _this.onEvent(new AudioSourceErrorEvent(errorMsg, "")); // initialization error - no streamid at this point


        return PromiseHelper.fromError(errorMsg);
      } else if (_this.privFile.name.lastIndexOf(".wav") !== _this.privFile.name.length - 4) {
        var _errorMsg = _this.privFile.name + " is not supported. Only WAVE files are allowed at the moment.";

        _this.onEvent(new AudioSourceErrorEvent(_errorMsg, ""));

        return PromiseHelper.fromError(_errorMsg);
      } else if (_this.privFile.size > FileAudioSource.MAX_SIZE) {
        var _errorMsg2 = _this.privFile.name + " exceeds the maximum allowed file size (" + FileAudioSource.MAX_SIZE + ").";

        _this.onEvent(new AudioSourceErrorEvent(_errorMsg2, ""));

        return PromiseHelper.fromError(_errorMsg2);
      }

      _this.onEvent(new AudioSourceInitializingEvent(_this.privId)); // no stream id


      _this.onEvent(new AudioSourceReadyEvent(_this.privId));

      return PromiseHelper.fromResult(true);
    };

    this.id = function () {
      return _this.privId;
    };

    this.attach = function (audioNodeId) {
      _this.onEvent(new AudioStreamNodeAttachingEvent(_this.privId, audioNodeId));

      return _this.upload(audioNodeId).onSuccessContinueWith(function (streamReader) {
        _this.onEvent(new AudioStreamNodeAttachedEvent(_this.privId, audioNodeId));

        return {
          detach: function detach() {
            streamReader.close();
            delete _this.privStreams[audioNodeId];

            _this.onEvent(new AudioStreamNodeDetachedEvent(_this.privId, audioNodeId));

            _this.turnOff();
          },
          id: function id() {
            return audioNodeId;
          },
          read: function read() {
            return streamReader.read();
          }
        };
      });
    };

    this.detach = function (audioNodeId) {
      if (audioNodeId && _this.privStreams[audioNodeId]) {
        _this.privStreams[audioNodeId].close();

        delete _this.privStreams[audioNodeId];

        _this.onEvent(new AudioStreamNodeDetachedEvent(_this.privId, audioNodeId));
      }
    };

    this.turnOff = function () {
      for (var streamId in _this.privStreams) {
        if (streamId) {
          var stream = _this.privStreams[streamId];

          if (stream && !stream.isClosed) {
            stream.close();
          }
        }
      }

      _this.onEvent(new AudioSourceOffEvent(_this.privId)); // no stream now


      return PromiseHelper.fromResult(true);
    };

    this.upload = function (audioNodeId) {
      return _this.turnOn().onSuccessContinueWith(function (_) {
        var stream = new Stream(audioNodeId);
        _this.privStreams[audioNodeId] = stream;
        var reader = new FileReader();
        var startOffset = 0;
        var endOffset = FileAudioSource.CHUNK_SIZE;

        var processNextChunk = function processNextChunk(event) {
          if (stream.isClosed) {
            return; // output stream was closed (somebody called TurnOff). We're done here.
          }

          stream.writeStreamChunk({
            buffer: reader.result,
            isEnd: false,
            timeReceived: Date.now()
          });

          if (endOffset < _this.privFile.size) {
            startOffset = endOffset;
            endOffset = Math.min(endOffset + FileAudioSource.CHUNK_SIZE, _this.privFile.size);

            var _chunk = _this.privFile.slice(startOffset, endOffset);

            reader.readAsArrayBuffer(_chunk);
          } else {
            // we've written the entire file to the output stream, can close it now.
            stream.close();
          }
        };

        reader.onload = processNextChunk;

        reader.onerror = function (event) {
          var errorMsg = "Error occurred while processing '".concat(_this.privFile.name, "'. ").concat(event);

          _this.onEvent(new AudioStreamNodeErrorEvent(_this.privId, audioNodeId, errorMsg));

          throw new Error(errorMsg);
        };

        var chunk = _this.privFile.slice(startOffset, endOffset);

        reader.readAsArrayBuffer(chunk);
        return stream.getReader();
      });
    };

    this.onEvent = function (event) {
      _this.privEvents.onEvent(event);

      Events.instance.onEvent(event);
    };

    this.privId = audioSourceId ? audioSourceId : createNoDashGuid();
    this.privEvents = new EventSource();
    this.privFile = file;
  }

  _createClass(FileAudioSource, [{
    key: "format",
    get: function get() {
      return FileAudioSource.FILEFORMAT;
    }
  }, {
    key: "events",
    get: function get() {
      return this.privEvents;
    }
  }, {
    key: "deviceInfo",
    get: function get() {
      return PromiseHelper.fromResult({
        bitspersample: FileAudioSource.FILEFORMAT.bitsPerSample,
        channelcount: FileAudioSource.FILEFORMAT.channels,
        connectivity: connectivity.Unknown,
        manufacturer: "Speech SDK",
        model: "File",
        samplerate: FileAudioSource.FILEFORMAT.samplesPerSec,
        type: type.File
      });
    }
  }]);

  return FileAudioSource;
}(); // Recommended sample rate (bytes/second).

FileAudioSource.SAMPLE_RATE = 16000 * 2; // 16 kHz * 16 bits
// We should stream audio at no faster than 2x real-time (i.e., send five chunks
// per second, with the chunk size == sample rate in bytes per second * 2 / 5).

FileAudioSource.CHUNK_SIZE = FileAudioSource.SAMPLE_RATE * 2 / 5; // 10 seconds of audio in bytes =
// sample rate (bytes/second) * 600 (seconds) + 44 (size of the wave header).

FileAudioSource.MAX_SIZE = FileAudioSource.SAMPLE_RATE * 600 + 44;
FileAudioSource.FILEFORMAT = AudioStreamFormat.getWaveFormatPCM(16000, 16, 1);